import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Loader2 } from "lucide-react";
import logo from "@/assets/rsg-white.png";
import AnimatedSection from "./AnimatedSection";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useRateLimit, formatLockoutTime } from "@/hooks/useRateLimit";

const NewsletterSection = () => {
  const { settings } = useSiteSettings();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formLoadTimeRef = useRef<number>(Date.now());
  const { toast } = useToast();

  const { isLocked, lockoutTimeRemaining, recordAttempt, canAttempt } = useRateLimit({
    maxAttempts: 3,
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
    storageKey: "newsletter_rate_limit",
  });

  // Reset form load time when component mounts
  useEffect(() => {
    formLoadTimeRef.current = Date.now();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side rate limit check
    if (!canAttempt() || isLocked) {
      toast({
        title: "Too many attempts",
        description: `Please try again in ${formatLockoutTime(lockoutTimeRemaining)}`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    const formLoadTime = Date.now() - formLoadTimeRef.current;

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: {
          name: name.trim(),
          email: email.trim(),
          honeypot,
          formLoadTime,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.error) {
        recordAttempt(false);
        toast({
          title: "Subscription failed",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      recordAttempt(true);
      toast({
        title: "Ahoy, Captain!",
        description: data?.message || "Check your email to confirm your subscription!",
      });
      setEmail("");
      setName("");
      // Reset form load time for next submission
      formLoadTimeRef.current = Date.now();
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      recordAttempt(false);
      toast({
        title: "Subscription failed",
        description: "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-dark bg-[#0a0a0a] py-32 relative overflow-hidden">
      {/* Background Image (CMS-managed or fallback skull) */}
      {settings.newsletter_bg_image ? (
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 pointer-events-none hidden md:block">
          <img
            src={settings.newsletter_bg_image}
            alt=""
            className="w-[500px] md:w-[700px] h-auto opacity-[0.06] rotate-12"
            aria-hidden="true"
            width={700}
            height={638}
            loading="lazy"
          />
        </div>
      ) : (
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 pointer-events-none hidden md:block">
          <img
            src={logo}
            alt=""
            className="w-[500px] md:w-[700px] h-auto opacity-[0.03] rotate-12"
            aria-hidden="true"
            width={700}
            height={638}
            loading="lazy"
          />
        </div>
      )}

      <div className="container max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <AnimatedSection animation="slide-left">
            <span className="section-label">Newsletter</span>
            <h2 className="section-title text-3xl md:text-4xl font-display font-bold text-dark-foreground mb-4">
              Captain's Log
            </h2>
            <p className="text-dark-foreground/60 text-lg leading-relaxed">
              Join our newsletter and stay up to date with news, releases, design updates, playtest insights, early
              access and many more treasures — no spam!
            </p>
          </AnimatedSection>

          {/* Form */}
          <AnimatedSection animation="slide-right" delay={100}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Honeypot field - hidden from real users */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] opacity-0 pointer-events-none"
              />

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                maxLength={100}
                autoComplete="name"
                aria-label="Your name"
                disabled={isSubmitting || isLocked}
                className="modern-input-dark flex-1"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                maxLength={254}
                autoComplete="email"
                aria-label="Your email address"
                disabled={isSubmitting || isLocked}
                className="modern-input-dark flex-1"
              />
              <button
                type="submit"
                disabled={isSubmitting || isLocked}
                className="btn-outline-light group whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Subscribing...
                  </>
                ) : isLocked ? (
                  `Wait ${formatLockoutTime(lockoutTimeRemaining)}`
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
            <p className="text-dark-foreground/40 text-xs mt-4">We respect your privacy. Unsubscribe at any time.</p>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
